import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import VerifiedBadge from '@/components/VerifiedBadge';
import VerificationDataCard from '@/components/VerificationDataCard';
import { Upload, CheckCircle2, Clock, AlertTriangle, FileText, Camera, Building, Scale, CreditCard, X, Shield, Loader2, Receipt } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DOC_META = {
  civ_membership: { en: 'CIV Membership', es: 'Membresia CIV', icon: Building, desc_en: 'Upload your Camara Inmobiliaria de Venezuela (CIV) membership certificate', desc_es: 'Suba su certificado de membresia de la Camara Inmobiliaria de Venezuela (CIV)' },
  rif: { en: 'RIF Document', es: 'Documento RIF', icon: FileText, desc_en: 'Upload your RIF (Registro de Informacion Fiscal)', desc_es: 'Suba su RIF (Registro de Informacion Fiscal)' },
  business_registration: { en: 'Business Registration', es: 'Registro Mercantil', icon: Building, desc_en: 'Upload your business registration document', desc_es: 'Suba su documento de registro mercantil' },
  cedula: { en: 'Cedula de Identidad', es: 'Cedula de Identidad', icon: CreditCard, desc_en: 'Upload a clear photo of both sides of your Cedula', desc_es: 'Suba una foto clara de ambos lados de su Cedula' },
  selfie: { en: 'Selfie Photo', es: 'Foto Selfie', icon: Camera, desc_en: 'Take a clear selfie holding your Cedula next to your face', desc_es: 'Tome una selfie clara sosteniendo su Cedula junto a su cara' },
  colegio_abogados: { en: 'Bar Association Registration', es: 'Registro del Colegio de Abogados', icon: Scale, desc_en: 'Upload your state-level Colegio de Abogados membership card', desc_es: 'Suba su credencial del Colegio de Abogados de su estado' },
  inpreabogado: { en: 'INPREABOGADO Affiliation', es: 'Afiliacion en INPREABOGADO', icon: Scale, desc_en: 'Upload your INPREABOGADO (Instituto de Prevision Social del Abogado) affiliation document', desc_es: 'Suba su afiliacion en INPREABOGADO (Instituto de Prevision Social del Abogado)' },
};

export default function VerificationPage() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [skuConfig, setSkuConfig] = useState(null);
  const [paidPayment, setPaidPayment] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };
  const tt = (en, es) => language === 'es' ? es : en;

  const verifySku = user?.role === 'lawyer' ? 'lawyer_verification' : 'agent_verification';
  const skuInfo = skuConfig?.[verifySku];
  const verifyPriceUsd = skuInfo ? (skuInfo.price_usd_cents / 100) : null;

  const fetchStatus = () => {
    axios.get(`${API}/verification/status`, { headers })
      .then(r => {
        setStatus(r.data);
        // Fire feedback trigger when the user is newly verified (backend gates once-per-user)
        if (r.data?.status === 'verified' && user?.role) {
          const key = `waka-verified-seen-${user.id}`;
          if (!localStorage.getItem(key)) {
            localStorage.setItem(key, '1');
            const trigger = user.role === 'lawyer' ? 'lawyer_verification_approved' : 'agent_verification_approved';
            setTimeout(() => { try { window.__wakaFeedback?.fire?.(trigger); } catch { /* ignore */ } }, 1500);
          }
        }
      })
      .catch(() => toast.error(tt('Failed to load verification status', 'Error al cargar estado de verificacion')))
      .finally(() => setLoading(false));
  };

  useEffect(() => { if (token) fetchStatus(); }, [token]);

  useEffect(() => {
    axios.get(`${API}/payments/config/public`)
      .then(r => setSkuConfig(r.data?.skus || {}))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/payments/mine`, { headers })
      .then(r => {
        const approved = (r.data?.payments || [])
          .filter(p => p.sku === verifySku && p.status === 'approved')
          .sort((a, b) => new Date(b.approved_at || b.created_at) - new Date(a.approved_at || a.created_at));
        setPaidPayment(approved[0] || null);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, verifySku]);

  const uploadDoc = async (docType, file) => {
    setUploading(docType);
    try {
      const formData = new FormData();
      formData.append('file', file);
      let fileUrl;
      try {
        const uploadRes = await axios.post(`${API}/upload`, formData, {
          headers: { ...headers, 'Content-Type': 'multipart/form-data' }
        });
        fileUrl = uploadRes.data.url;
      } catch {
        fileUrl = `uploaded://${file.name}`;
      }
      await axios.post(`${API}/verification/upload-doc`, {
        doc_type: docType, url: fileUrl, file_name: file.name
      }, { headers });
      toast.success(tt('Document uploaded!', 'Documento subido!'));
      fetchStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || tt('Upload failed', 'Error al subir'));
    }
    setUploading('');
  };

  const submitForReview = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/verification/submit`, {}, { headers });
      toast.success(tt('Submitted for verification!', 'Enviado para verificacion!'));
      try { window.__wakaFeedback?.fire?.('verification_submitted'); } catch { /* ignore */ }
      fetchStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || tt('Submission failed', 'Error al enviar'));
    }
    setSubmitting(false);
  };

  if (loading) return (
    <div className="max-w-3xl mx-auto px-4 py-8 space-y-4">
      <Skeleton className="h-10 w-64" />
      <Skeleton className="h-48 rounded-xl" />
    </div>
  );

  if (!user || !['agent', 'lawyer'].includes(user.role)) return (
    <div className="max-w-3xl mx-auto px-4 py-16 text-center">
      <Shield className="w-12 h-12 text-slate-300 mx-auto mb-3" />
      <p className="text-slate-500 font-semibold">{tt('Verification is available for agents and lawyers only.', 'La verificacion esta disponible solo para agentes y abogados.')}</p>
    </div>
  );

  const requiredDocs = status?.required_docs || [];
  const uploadedDocs = status?.uploaded_docs || [];
  const allUploaded = requiredDocs.every(d => uploadedDocs.includes(d));
  const verificationStatus = status?.status || 'not_started';

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="verification-page">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3">
          <Shield className="w-7 h-7 text-primary" />
          <h1 className="font-heading text-xl md:text-2xl font-semibold">
            {user.role === 'agent' ? tt('Agent Verification', 'Verificacion de Agente') : tt('Lawyer Verification', 'Verificacion de Abogado')}
          </h1>
        </div>
        <p className="text-sm text-slate-500 mt-2 ml-10">
          {tt('Get your verification badge to build trust with clients. Valid for 1 year.', 'Obtenga su insignia de verificacion para generar confianza con los clientes. Valida por 1 ano.')}
        </p>
      </div>

      {/* Pricing CTA — hidden if user already paid OR is fully verified */}
      {verificationStatus !== 'verified' && !paidPayment && (
        <div className="border-2 border-[#D4AF37]/40 bg-gradient-to-br from-amber-50 to-white rounded-xl p-5 mb-6 flex items-center justify-between gap-4 flex-wrap" data-testid="agent-verify-pricing-cta">
          <div className="flex items-start gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-[#D4AF37] flex items-center justify-center flex-shrink-0">
              <Shield className="w-5 h-5 text-[#0B192C]" />
            </div>
            <div className="min-w-0">
              <p className="font-semibold text-[#0B192C]">
                {tt('Verification Fee — One-time payment', 'Tarifa de Verificación — Pago Único')}
              </p>
              <p className="text-xs text-slate-600 mt-0.5 max-w-md">
                {tt('Get the gold Verified badge — valid 12 months. Boosts trust, top placement in search, +25 trust score.', 'Obtén el sello dorado Verificado — válido 12 meses. Aumenta confianza, mejor posicionamiento, +25 puntos de confianza.')}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <div className="text-right">
              <div className="font-heading text-2xl font-bold text-[#0B192C]" data-testid="verify-price-display">
                USD ${verifyPriceUsd != null ? verifyPriceUsd.toFixed(0) : '49'}
              </div>
              <div className="text-[10px] text-slate-500">{tt('one-time / 12 months', 'pago único / 12 meses')}</div>
            </div>
            <button
              onClick={() => navigate(`/payments/checkout?sku=${verifySku}`)}
              className="bg-[#D4AF37] hover:bg-[#C8A028] text-[#0B192C] font-semibold px-4 py-2 rounded-md inline-flex items-center text-sm"
              data-testid="agent-verify-pay-btn"
            >
              {tt('Pay verification →', 'Pagar verificación →')}
            </button>
          </div>
        </div>
      )}

      {/* Paid — show payment details */}
      {paidPayment && verificationStatus !== 'verified' && (
        <Card className="border-2 border-emerald-300 bg-emerald-50 mb-6" data-testid="verify-payment-details-card">
          <CardContent className="p-5">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-emerald-200 flex items-center justify-center flex-shrink-0">
                <Receipt className="w-5 h-5 text-emerald-700" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-emerald-900 text-sm">
                  {tt('Verification payment approved', 'Pago de verificación aprobado')}
                </p>
                <p className="text-xs text-emerald-700 mt-0.5 max-w-2xl">
                  {tt(
                    'Your payment is confirmed. Upload all required documents below to complete verification.',
                    'Tu pago está confirmado. Sube todos los documentos requeridos para completar la verificación.'
                  )}
                </p>
              </div>
              <Badge className="bg-emerald-600 text-white text-[10px] uppercase tracking-wider" data-testid="payment-status-badge">
                {tt('Approved', 'Aprobado')}
              </Badge>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
              <div>
                <p className="text-emerald-700 font-medium">{tt('Amount', 'Monto')}</p>
                <p className="font-semibold text-[#0B192C] mt-0.5" data-testid="paid-amount">
                  USD ${((paidPayment.amount_usd_cents || 0) / 100).toFixed(2)}
                </p>
              </div>
              <div>
                <p className="text-emerald-700 font-medium">{tt('Method', 'Método')}</p>
                <p className="font-semibold text-[#0B192C] mt-0.5 capitalize" data-testid="paid-method">
                  {(paidPayment.method || '-').replace(/_/g, ' ')}
                </p>
              </div>
              <div>
                <p className="text-emerald-700 font-medium">{tt('Reference', 'Referencia')}</p>
                <p className="font-mono text-[11px] font-semibold text-[#0B192C] mt-0.5 truncate" title={paidPayment.reference_code || ''} data-testid="paid-reference">
                  {paidPayment.reference_code || '-'}
                </p>
              </div>
              <div>
                <p className="text-emerald-700 font-medium">{tt('Approved on', 'Aprobado')}</p>
                <p className="font-semibold text-[#0B192C] mt-0.5" data-testid="paid-approved-at">
                  {paidPayment.approved_at
                    ? new Date(paidPayment.approved_at).toLocaleDateString()
                    : (paidPayment.created_at ? new Date(paidPayment.created_at).toLocaleDateString() : '-')}
                </p>
              </div>
            </div>
            <button
              onClick={() => navigate(`/payments`)}
              className="mt-4 text-[11px] font-medium text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-1"
              data-testid="view-all-payments-link"
            >
              {tt('View receipt →', 'Ver recibo →')}
            </button>
          </CardContent>
        </Card>
      )}

      {/* Status Banner */}
      {verificationStatus === 'verified' && (
        <div className="bg-green-50 border-2 border-green-200 rounded-xl p-5 mb-6 flex items-center gap-4" data-testid="verification-verified-banner">
          <VerifiedBadge role={user.role} size="md" />
          <div>
            <p className="font-semibold text-green-800">{tt('You are verified!', 'Estas verificado!')}</p>
            <p className="text-xs text-green-600 mt-0.5">
              {tt('Valid until', 'Valido hasta')}: {status?.expires_at ? new Date(status.expires_at).toLocaleDateString() : '-'}
            </p>
          </div>
        </div>
      )}
      {verificationStatus === 'under_review' && (
        <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-5 mb-6 flex items-center gap-4" data-testid="verification-review-banner">
          <Clock className="w-6 h-6 text-blue-600" />
          <div>
            <p className="font-semibold text-blue-800">{tt('Under Review', 'En Revision')}</p>
            <p className="text-xs text-blue-600">{tt('Your documents are being verified. AI comparison + admin review.', 'Sus documentos estan siendo verificados. Comparacion IA + revision admin.')}</p>
          </div>
        </div>
      )}
      {verificationStatus === 'rejected' && (
        <div className="bg-red-50 border-2 border-red-200 rounded-xl p-5 mb-6" data-testid="verification-rejected-banner">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-6 h-6 text-red-600" />
            <div>
              <p className="font-semibold text-red-800">{tt('Verification Rejected', 'Verificacion Rechazada')}</p>
              {status?.admin_notes && <p className="text-xs text-red-600 mt-0.5">{status.admin_notes}</p>}
            </div>
          </div>
          <p className="text-xs text-red-500 mt-2 ml-9">{tt('You can re-upload documents and submit again.', 'Puede volver a subir documentos y enviar de nuevo.')}</p>
        </div>
      )}

      {/* AI Result */}
      {status?.ai_result && verificationStatus === 'under_review' && (
        <Card className="mb-6" data-testid="ai-result-card">
          <CardContent className="p-4">
            <p className="text-xs font-semibold text-slate-500 uppercase mb-2">{tt('AI Verification Result', 'Resultado de Verificacion IA')}</p>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-slate-400 text-xs">{tt('Face Match', 'Coincidencia Facial')}</span>
                <p className={`font-medium ${status.ai_result.face_match === 'match' ? 'text-green-700' : status.ai_result.face_match === 'no_match' ? 'text-red-700' : 'text-amber-700'}`}>
                  {status.ai_result.face_match === 'match' ? tt('Match', 'Coincide') : status.ai_result.face_match === 'no_match' ? tt('No Match', 'No Coincide') : tt('Uncertain', 'Incierto')}
                </p>
              </div>
              <div>
                <span className="text-slate-400 text-xs">{tt('Confidence', 'Confianza')}</span>
                <p className="font-medium capitalize">{status.ai_result.confidence || '-'}</p>
              </div>
              {status.ai_result.name_extracted && (
                <div className="col-span-2">
                  <span className="text-slate-400 text-xs">{tt('Name Extracted', 'Nombre Extraido')}</span>
                  <p className="font-medium">{status.ai_result.name_extracted}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Document Upload Cards — gated behind approved payment */}
      {verificationStatus !== 'verified' && !paidPayment && (
        <Card className="border-2 border-slate-200 bg-slate-50 mb-6" data-testid="verify-upload-gate">
          <CardContent className="p-6 text-center">
            <div className="w-12 h-12 mx-auto rounded-2xl bg-slate-200 flex items-center justify-center mb-3">
              <Shield className="w-6 h-6 text-slate-400" />
            </div>
            <p className="font-semibold text-[#0B192C]">
              {tt('Document upload unlocks after payment', 'La carga de documentos se habilita tras el pago')}
            </p>
            <p className="text-xs text-slate-500 mt-1 max-w-md mx-auto">
              {tt(
                'Pay the verification fee above to unlock the document upload form. Until then, no documents can be submitted.',
                'Paga la tarifa de verificación arriba para habilitar el formulario de carga. Hasta entonces, no se pueden enviar documentos.'
              )}
            </p>
          </CardContent>
        </Card>
      )}

      {verificationStatus !== 'verified' && paidPayment && (
        <div className="space-y-3">
          <h2 className="font-heading font-semibold text-base">{tt('Required Documents', 'Documentos Requeridos')}</h2>
          {requiredDocs.map(docType => {
            const meta = DOC_META[docType] || { en: docType, es: docType, icon: FileText, desc_en: '', desc_es: '' };
            const Icon = meta.icon;
            const isUploaded = uploadedDocs.includes(docType);
            const isCurrentlyUploading = uploading === docType;
            const docData = status?.documents?.[docType];

            return (
              <Card key={docType} className={`transition-all ${isUploaded ? 'border-green-200 bg-green-50/30' : ''}`} data-testid={`doc-card-${docType}`}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${isUploaded ? 'bg-green-100' : 'bg-slate-100'}`}>
                      {isUploaded ? <CheckCircle2 className="w-5 h-5 text-green-600" /> : <Icon className="w-5 h-5 text-slate-400" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm">{language === 'es' ? meta.es : meta.en}</p>
                      <p className="text-xs text-slate-400 mt-0.5">{language === 'es' ? meta.desc_es : meta.desc_en}</p>
                      {docData && (
                        <p className="text-[10px] text-green-600 mt-1">
                          {docData.file_name || tt('Uploaded', 'Subido')} - {new Date(docData.uploaded_at).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                    <div className="flex-shrink-0">
                      {verificationStatus !== 'under_review' && (
                        <label>
                          <Button size="sm" variant={isUploaded ? 'outline' : 'default'}
                            className={`gap-1.5 cursor-pointer ${!isUploaded ? 'bg-[#D4AF37] hover:bg-[#C8A028] text-[#0B192C]' : ''}`}
                            asChild disabled={isCurrentlyUploading}
                            data-testid={`upload-btn-${docType}`}>
                            <span>
                              {isCurrentlyUploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                              {isUploaded ? tt('Replace', 'Reemplazar') : tt('Upload', 'Subir')}
                            </span>
                          </Button>
                          <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.webp"
                            onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadDoc(docType, f); e.target.value = ''; }} />
                        </label>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Submit Button */}
      {allUploaded && (verificationStatus === 'documents_uploaded' || verificationStatus === 'not_started' || verificationStatus === 'rejected') && (
        <div className="mt-6">
          <Button onClick={submitForReview} disabled={submitting}
            className="w-full bg-[#D4AF37] hover:bg-[#C8A028] text-[#0B192C] gap-2 h-12 text-base" data-testid="submit-verification-btn">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Shield className="w-4 h-4" />}
            {submitting ? tt('Submitting...', 'Enviando...') : tt('Submit for Verification', 'Enviar para Verificacion')}
          </Button>
          <p className="text-xs text-slate-400 text-center mt-2">{tt('AI will compare your selfie with your Cedula. An admin will do the final review.', 'La IA comparara su selfie con su Cedula. Un administrador hara la revision final.')}</p>
        </div>
      )}

      {/* Progress indicator */}
      {paidPayment && verificationStatus !== 'verified' && (
        <div className="mt-8 pt-6 border-t border-border">
          <p className="text-xs font-semibold text-slate-400 uppercase mb-3">{tt('Progress', 'Progreso')}</p>
          <div className="flex items-center gap-2">
            {requiredDocs.map((d, i) => (
              <div key={d} className={`h-2 flex-1 rounded-full ${uploadedDocs.includes(d) ? 'bg-green-400' : 'bg-slate-200'}`} />
            ))}
          </div>
          <p className="text-xs text-slate-400 mt-2">{uploadedDocs.length}/{requiredDocs.length} {tt('documents uploaded', 'documentos subidos')}</p>
        </div>
      )}

      {/* AI-extracted verification data (private — visible only to self & superadmin) */}
      <div className="mt-8">
        <VerificationDataCard mode="self" />
      </div>
    </div>
  );
}
